import { ComposerFilesState } from 'components/ComposerMediaContext'
import {
  Article,
  ARTICLE_TYPE,
  ContentItem,
  Gif,
  GIF_TYPE,
  Photo,
  PHOTO_TYPE,
  Video,
  VIDEO_TYPE
} from 'interfaces'
import { Observable } from 'rxjs'
import { tap } from 'rxjs/operators'
import { withPostTypeConstraints } from 'services/compose/tiktok/utils'
import { getBuckets } from 'services/post/actions'
import { defaultTimezoneSelector } from 'services/settings/selectors'
import {
  BRAND_BLUESKY,
  BRAND_FACEBOOK,
  BRAND_GOOGLE,
  BRAND_INSTAGRAM,
  BRAND_LINKEDIN,
  BRAND_PINTEREST,
  BRAND_THREADS,
  BRAND_TIKTOK,
  BRAND_TWITTER,
  BRAND_YOUTUBE,
  CreatePostRequestData
} from 'shared'
import {
  ComposerBlueskyPost,
  ComposerFacebookPost,
  ComposerGooglePost,
  ComposerInstagramPost,
  ComposerLinkedinPost,
  ComposerPinterestPost,
  ComposerThreadsPost,
  ComposerTikTokPost,
  ComposerTwitterPost,
  ComposerYoutubePost,
  GOOGLE_POST_TYPE_EVENT,
  GOOGLE_POST_TYPE_OFFER,
  GOOGLE_POST_TYPE_UPDATE,
  PostDestinationType
} from 'shared/types'
import { getState, StoreThunkAction, StoreThunkDispatch } from 'store/state'
import { getFBPostTextFromHtml, getLinkedinFormattedText, getTagsInfoFromHtml } from 'utils/composer'
import {
  activePostIdSelector,
  aiWriterPromptIdSelector,
  composerAltTextEnabledSelector,
  composerAltTextSelector,
  composerBulkFileUrlSelector,
  composerContentItemSelector,
  composerDocumentUploadSelector,
  composerImageUrlsSelector,
  composerInstagramCoverUrlSelector,
  composerIsAiContentSelector,
  composerLinkSelector,
  composerPostAsPhotoSelector,
  composerPostSelector,
  composerRecycleSelector,
  composerScheduleSelector,
  composerSelectedProfilesSelector,
  composerVideoThumbnailOffsetSelector,
  composerVideoThumbnailSelector,
  composerVideoUrlSelector,
  composerWithImagesSelector,
  featuredImageUrlSelector,
  pinterestProfilesBoardsSelector
} from '../selectors'
import { getPostScheduleFromComposerState, toPinterestAiContentMode } from '../utils'
import { createPostFailure, createPostRequest, createPostSuccess, trackPostCreated } from './actions'
import { V1 } from './net'

export function getPostData(
  shareType?: 'now' | 'next' | 'queue',
  scheduled?: boolean,
  uploads?: ComposerFilesState
): StoreThunkAction<CreatePostRequestData> {
  return (_dispatch: StoreThunkDispatch, getState: getState) => {
    const state = getState()
    const recycle = composerRecycleSelector(state)
    const profiles = composerSelectedProfilesSelector(state)
    const photos = composerImageUrlsSelector(state).slice() // copy the array so we can sort it
    const videoUrl = composerVideoUrlSelector(state)
    const videoThumbnail = composerVideoThumbnailSelector(state)
    const videoThumbnailOffset = composerVideoThumbnailOffsetSelector(state)
    const instagramCoverUrl = composerInstagramCoverUrlSelector(state)
    const contentItem = composerContentItemSelector(state)
    const bulkUploadFileUrl = composerBulkFileUrlSelector(state)
    const link = composerLinkSelector(state)
    const featuredImageUrl = featuredImageUrlSelector(state)
    const pinterestBoards = pinterestProfilesBoardsSelector(state)
    const networks: PostDestinationType[] = []
    const ppPageIds: number[] = []
    const composerSchedule = composerScheduleSelector(state)
    const timezone = defaultTimezoneSelector(state)
    const postAsPhoto = composerPostAsPhotoSelector(state)
    const aiWriterPromptId = aiWriterPromptIdSelector(state)
    const doc = composerDocumentUploadSelector(state)
    const altTextEnabled = composerAltTextEnabledSelector(state)
    const altText = composerAltTextSelector(state)
    const withImages = composerWithImagesSelector(state)
    const isEditingPost = Boolean(activePostIdSelector(state))
    const isAiContent = composerIsAiContentSelector(state)

    const files: any = Object.values(uploads || {}).reduce((map: Record<string, any>, file: any) => {
      if (file.url) {
        map[file.url] = file.order
      }
      return map
    }, {})

    if (Object.keys(files).length > 0 && photos.length > 0) {
      photos.sort((url1, url2) => {
        if (files[url1] < files[url2]) {
          return -1
        }
        return 1
      })
    }

    profiles.forEach(profile => {
      ppPageIds.push(parseInt(profile.ppid, 10))
      if (!networks.includes(profile.type)) {
        networks.push(profile.type)
      }
    })

    const data: CreatePostRequestData = {
      postType: 'status',
      ppPageIds,
      networks: {}
    }

    if (shareType) {
      data.shareType = shareType
    }

    if (featuredImageUrl) {
      data.featuredImageUrl = featuredImageUrl
    }

    // EXPL: Alt text describes an image, so it only travels with a post that has one. The control is
    // unreachable in bulk mode and switching modes does not reset the composer, hence the bulk guard.
    if (withImages && !bulkUploadFileUrl) {
      const altTextValue = altTextEnabled ? altText.trim() : ''
      // Send the empty value when editing so an existing alt text can be cleared, but keep it off
      // the payload on create where there is nothing to overwrite.
      if (altTextValue || isEditingPost) {
        data.altText = altTextValue
      }
    }

    if (scheduled) {
      data.schedule = getPostScheduleFromComposerState(composerSchedule, timezone)
    }

    if (videoUrl) {
      data.postType = 'video'
      data.videoUrl = videoUrl
      if (videoThumbnail) {
        data.videoThumbnail = videoThumbnail
      }
      if (videoThumbnailOffset) {
        data.videoThumbnailOffset = videoThumbnailOffset
        data.videoThumbnailOffsetUrl = videoThumbnail
      }
      if (instagramCoverUrl) {
        data.videoThumbnailUrl = instagramCoverUrl
      }
    }

    if (recycle) {
      data.recycle = true
      if (typeof recycle === 'object') {
        data.recycleUntil = recycle.toISOString()
      } else if (typeof recycle === 'number') {
        data.recycleTimes = recycle
      }
    }

    if (bulkUploadFileUrl) {
      data.postType = 'bulk'
      data.bulkUploadFileUrl = bulkUploadFileUrl
    }

    if (photos.length > 0) {
      data.postType = 'photo'
      data.photos = photos
    }

    if (contentItem) {
      let date = (contentItem as ContentItem).createdAt
      if (typeof date === 'number' || typeof date === 'string') {
        date = new Date(date).toISOString()
        data.sourcePost = { date }
      } else {
        data.sourcePost = { date: date.toISOString() }
      }
      data.sourcePost.feedName = contentItem.feed.name
      data.sourcePost.feedType = contentItem.feed.type
      data.sourcePost.socialLink = contentItem.socialLink
      data.sourcePost.sourceLink = (contentItem as any).sourceLink || contentItem.socialLink
    }

    if (contentItem?.type === PHOTO_TYPE) {
      data.postType = 'photo'
      data.photos = [(contentItem as Photo).imageUrl]
    } else if (contentItem?.type === ARTICLE_TYPE && link?.type !== 'link') {
      data.postType = postAsPhoto ? 'photo' : 'link'
      data.photos = [(contentItem as Photo).imageUrl]
      data.link = {
        title: (contentItem as Article).title,
        description: (contentItem as Article).description,
        url: (contentItem as Article).sourceLink
      }
    } else if (contentItem?.type === VIDEO_TYPE) {
      data.postType = 'video_link'
      data.photos = [(contentItem as Video).imageUrl]
      data.link = {
        title: (contentItem as Video).title,
        description: (contentItem as Video).description,
        url: contentItem.socialLink
      }
    } else if (contentItem?.type === GIF_TYPE) {
      const gifUrl = (contentItem as Gif).gifUrl.split('?')[0]
      const ext = gifUrl.substring(gifUrl.lastIndexOf('.')).toLowerCase()
      data.postType = 'gif'
      // NOTE: Twitter gifs are actually mp4 videos
      if (ext === '.mp4') {
        data.postType = 'video'
        data.videoUrl = gifUrl
      }
    } else if (link) {
      data.postType = postAsPhoto ? 'photo' : link.type
      data.link = {
        title: link.title,
        url: link.url,
        description: link.description
      }
      const linkImage = link.selectedImageUrl || link.image[0]
      data.photos = !data.photos || data.photos.length === 0 ? [linkImage] : data.photos
    }

    if (aiWriterPromptId) {
      data.sourceFeature = `prompt_${aiWriterPromptId}`
    }

    networks.forEach(network => {
      const post = composerPostSelector(state, network)
      switch (network) {
        case BRAND_FACEBOOK: {
          const status = getFBPostTextFromHtml(post.status.html)
          const tags = getTagsInfoFromHtml(post.status.html)
          const facebookPost: any = { status, details: {} }
          const fbData = post as ComposerFacebookPost
          const selectedAlbums = fbData.albums
          const comments = Array.isArray(fbData.comments) ? fbData.comments : []
          const formattedComments = comments
            .filter(comment => comment.text?.trim())
            .map(comment => ({
              text: getFBPostTextFromHtml(comment.html).trim(),
              tags: getTagsInfoFromHtml(comment.html),
              delay: comment.delay || 0
            }))

          if (formattedComments.length > 0) {
            facebookPost.comments = formattedComments
          }
          const postType = fbData.postType
          const textBackground = fbData.textBackground

          facebookPost.comments = formattedComments
          if (textBackground) {
            facebookPost.details.textBackground = textBackground
          }

          if (postType) {
            facebookPost.details.postType = postType
          }

          if (tags.length > 0) {
            facebookPost.details.tags = tags
          }
          if (selectedAlbums.length > 0) {
            facebookPost.details.albums = selectedAlbums.map(a => ({ ...a, ppPageId: a.ppid }))
          }

          // EXPL: always sent - `composer_new` replaces the whole post, so an omitted key un-flags it on edit
          facebookPost.details.isAiContent = isAiContent

          if (fbData.reshare) {
            facebookPost.reshare = true
            if (contentItem?.feed.type === BRAND_FACEBOOK) {
              facebookPost.details.reshareUrl = contentItem.socialLink
              facebookPost.details.reshareName = contentItem.feed.name
            }
          }

          data.networks.facebook = facebookPost
          break
        }
        case BRAND_TWITTER: {
          const status = post.status.text
          const twitterPost: any = {
            status,
            details: {
              tweetType: (post as ComposerTwitterPost).tweetType
            }
          }
          const comments = (post as ComposerTwitterPost).comments
          const formattedComments = comments.filter(comment => comment.text?.trim())

          if (formattedComments.length > 0) {
            twitterPost.comments = formattedComments
          }
          // EXPL: always sent - `composer_new` replaces the whole post, so an omitted key un-flags it on edit
          twitterPost.details.isAiContent = isAiContent
          if ((contentItem as ContentItem)?.feed.type === BRAND_TWITTER) {
            twitterPost.details.tweetUrl = (contentItem as ContentItem).socialLink
            data.sourcePost = data.sourcePost || {}
            data.sourcePost.feedName = (contentItem as ContentItem).feed.name
            data.sourcePost.text = (contentItem as ContentItem).tweetText
          }
          data.networks.twitter = twitterPost
          break
        }
        case BRAND_BLUESKY: {
          const { status, comments, facets } = post as ComposerBlueskyPost
          const tags = getTagsInfoFromHtml(post.status.html)
          const bsComments = comments.filter(comment => comment.text?.trim())
          data.networks.bluesky = { status: status.text, details: {} }

          if (facets) {
            data.networks.bluesky.details!.facets = facets
          }
          if (bsComments.length > 0) {
            data.networks.bluesky.comments = comments
          }
          if (tags.length > 0) {
            data.networks.bluesky.details!.tags = tags
          }
          break
        }

        case BRAND_INSTAGRAM: {
          const instagramPost: any = { status: post.status.text, details: {} }
          const { comments, postType: igPostType, locations, tags, coverUrl } = post as ComposerInstagramPost
          const igComments = comments.filter(comment => comment.text?.trim())
          const locationsArray = Object.keys(locations).reduce((arr: any[], ppid) => {
            if (locations[ppid]) {
              arr.push({ ...locations[ppid], ppPageId: ppid })
            }
            return arr
          }, [])

          if (igComments.length > 0) {
            instagramPost.comments = comments
          }
          if (locationsArray.length > 0) {
            instagramPost.details.locations = locationsArray
          }
          if (igPostType) {
            instagramPost.details.postType = igPostType
          }
          if (tags && !videoUrl && photos.length === 1) {
            instagramPost.details.photoTags = tags
          }
          if (coverUrl && videoUrl) {
            instagramPost.details.coverUrl = coverUrl
          }
          // EXPL: always sent - `composer_new` replaces the whole post, so an omitted key un-flags it on edit
          instagramPost.details.isAiContent = isAiContent
          data.networks.instagram = instagramPost
          break
        }
        case BRAND_PINTEREST: {
          const pin = post as ComposerPinterestPost

          const pinterestPost: any = {
            status: pin.title,
            description: pin.description,
            destinationUrl: postAsPhoto ? undefined : pin.destinationUrl,
            details: { boards: [] }
          }
          pinterestPost.piAiContentMode = toPinterestAiContentMode(isAiContent, pin.aiContentMode)
          // EXPL: always sent - `composer_new` replaces the whole post, so an omitted key un-flags it on edit.
          // Any mode flags the pin - an AI-generated person is AI content even with the composer flag off.
          pinterestPost.details.isAiContent = pinterestPost.piAiContentMode !== null
          if (Object.keys(pinterestBoards).length > 0) {
            const boardsArray = Object.keys(pinterestBoards).reduce((arr: any[], ppid: string) => {
              const selectedBoard = pinterestBoards[ppid].find(b => b.selected)
              if (selectedBoard) {
                arr.push({
                  id: selectedBoard.id,
                  name: selectedBoard.name,
                  ppPageId: ppid,
                  url: selectedBoard.url
                })
              }

              return arr
            }, [])
            pinterestPost.details.boards = boardsArray
          }
          data.networks.pinterest = pinterestPost
          break
        }
        case BRAND_LINKEDIN: {
          const { tags, text } = getLinkedinFormattedText(post.status.html)
          const comments = (post as ComposerLinkedinPost).comments.filter(comment => comment.text?.trim())

          const linkedInPost: any = {
            status: post.status.text,
            details: { tags, text }
          }
          if (comments.length > 0) {
            linkedInPost.comments = comments
          }
          if (doc) {
            linkedInPost.details.documentUrl = doc.url
            linkedInPost.details.documentName = doc.name
          }
          data.networks.linkedin = linkedInPost
          break
        }
        case BRAND_GOOGLE: {
          const gPost = post as ComposerGooglePost
          data.networks.google = {
            status: post.status.text,
            details: { postType: gPost.selectedPostType }
          }
          switch (gPost.selectedPostType) {
            case GOOGLE_POST_TYPE_UPDATE:
              data.networks.google.details!.buttonType = gPost.update.buttonType
              data.networks.google.details!.buttonUrl = gPost.update.link
              break

            case GOOGLE_POST_TYPE_OFFER:
              data.networks.google.details!.couponCode = gPost.offer.offer.couponCode
              data.networks.google.details!.redeemOnlineUrl = gPost.offer.offer.redeemOnlineUrl
              data.networks.google.details!.termsConditions = gPost.offer.offer.termsConditions
              data.networks.google.details!.title = gPost.offer.event.title
              data.networks.google.details!.startDate = gPost.offer.event.schedule.startDate
              data.networks.google.details!.endDate = gPost.offer.event.schedule.endDate
              break

            case GOOGLE_POST_TYPE_EVENT:
              data.networks.google.details!.title = gPost.event.title
              data.networks.google.details!.startDate = gPost.event.schedule.startDate
              data.networks.google.details!.endDate = gPost.event.schedule.endDate
              break
          }
          break
        }
        case BRAND_TIKTOK: {
          // Duet and Stitch are video-only. The settings popup that strips them is mounted lazily and may never have
          // been opened, so enforce it here, where the post type is known for certain.
          // The composer AI content checkbox owns the flag - whatever the profile settings carry is overridden.
          const tkSettings = Object.values((post as ComposerTikTokPost).settings)
            .map(profileSettings => ({ ...withPostTypeConstraints(profileSettings, !videoUrl), isAiContent }))
          data.networks.tiktok = {
            status: post.status.text,
            details: {
              settings: tkSettings
            }
          }

          if (photos.length > 0) {
            data.networks.tiktok.title = (post as ComposerTikTokPost).title
            data.networks.tiktok.description = (post as ComposerTikTokPost).description
          }
          break
        }
        case BRAND_YOUTUBE: {
          const ytPost = post as ComposerYoutubePost
          const comments = ytPost.comments.filter(comment => comment.text?.trim())

          data.networks.youtube = {
            status: ytPost.title,
            description: ytPost.description,
            details: {
              privacyStatus: ytPost.privacyStatus,
              // EXPL: always sent - `composer_new` replaces the whole post, so an omitted key un-flags it on edit
              isAiContent
            }
          }

          if (comments.length > 0) {
            data.networks.youtube.comments = comments
          }
          break
        }
        case BRAND_THREADS: {
          const threadsPost = { status: post.status.text } as any
          const comments = (post as ComposerThreadsPost).comments.filter(comment => comment.text?.trim())
          if (comments.length > 0) {
            threadsPost.comments = comments
          }
          data.networks.threads = threadsPost
          break
        }
      }
    })
    return data
  }
}

export function createPost(data: CreatePostRequestData, bucketId?: string, withContent?: boolean): StoreThunkAction<Observable<any>> {
  return (dispatch) => {
    const id = Date.now().toString()
    dispatch(createPostRequest({ post: data, id }))
    return dispatch(V1.createPost(data as any, bucketId)).pipe(
      tap((response: any) => {
        dispatch(trackPostCreated(data, response, bucketId, withContent))
        dispatch(createPostSuccess({ post: data, id }))
        if (bucketId) {
          dispatch(getBuckets())
        }
      }, (error) => {
        dispatch(createPostFailure({ error: error.response?.error || error, source: { post: data, id } }))
      })
    )
  }
}
